import { getConfig } from "config";
import { buildUrl } from "services/url";

export type ContextlessDropoffStatus = "deposited" | "already_deposited";

export interface ContextlessDropoffResponse {
  status: ContextlessDropoffStatus;
  reservation_id: string;
  locker_id: string;
  door_position: string;
}

export type ContextlessDropoffErrorKind = "timeout" | "network" | "http" | "invalid_response";

export type ContextlessDropoffRecovery =
  | "retry_same_attempt"
  | "manual_door_verification"
  | "safe_exit";

type ContextlessDropoffErrorOptions = {
  kind: ContextlessDropoffErrorKind;
  status?: number;
  code?: string;
  recovery: ContextlessDropoffRecovery;
  retryable?: boolean;
  ambiguous?: boolean;
};

export class ContextlessDropoffError extends Error {
  kind: ContextlessDropoffErrorKind;
  status?: number;
  code?: string;
  recovery: ContextlessDropoffRecovery;
  retryable: boolean;
  ambiguous: boolean;

  constructor(options: ContextlessDropoffErrorOptions) {
    super("Contextless dropoff failed");
    this.name = "ContextlessDropoffError";
    this.kind = options.kind;
    this.status = options.status;
    this.code = options.code;
    this.recovery = options.recovery;
    this.retryable = options.retryable ?? options.recovery === "retry_same_attempt";
    this.ambiguous = options.ambiguous ?? options.recovery !== "safe_exit";
  }
}

type CreateContextlessDropoffRequest = {
  scan: string;
  idempotencyKey: string;
  timeoutMs?: number;
  onProgress?: (phase: "found") => void;
  progressSignal?: AbortSignal;
};

export const CONTEXTLESS_DROPOFF_PROGRESS_INTERVAL_MS = 750;

/** Advisory reads never determine the mutation outcome or retain its payload. */
function observeProgress(
  url: string,
  headers: Record<string, string>,
  onProgress: (phase: "found") => void,
  signal?: AbortSignal
) {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let deadline: ReturnType<typeof setTimeout> | undefined;
  let request: AbortController | undefined;
  const stop = () => {
    stopped = true;
    clearTimeout(timer);
    clearTimeout(deadline);
    request?.abort();
    signal?.removeEventListener("abort", stop);
  };
  const poll = async () => {
    if (stopped) return;
    request = new AbortController();
    deadline = setTimeout(() => request?.abort(), 2500);
    try {
      const response = await fetch(url, {
        method: "GET",
        headers,
        cache: "no-store",
        signal: request.signal,
      });
      if (stopped) return;
      if (response.status === 404) {
        stop(); // Older Admin versions do not expose progress.
        return;
      }
      if (response.ok) {
        const body: unknown = await response.json();
        if (!stopped && (body as { phase?: unknown } | null)?.phase === "found") {
          stop(); // Found is monotonic; only the POST can resolve this operation.
          onProgress("found");
        }
      }
    } catch {
      // Progress transport, parsing and consumer errors never affect the POST.
    } finally {
      clearTimeout(deadline);
      if (!stopped) timer = setTimeout(() => void poll(), CONTEXTLESS_DROPOFF_PROGRESS_INTERVAL_MS);
    }
  };
  signal?.addEventListener("abort", stop);
  if (signal?.aborted) stop();
  else timer = setTimeout(() => void poll(), CONTEXTLESS_DROPOFF_PROGRESS_INTERVAL_MS);
  return stop;
}

/**
 * This is an observation timeout, not an upper bound on Admin or Virtual work.
 * Aborting the browser request can leave the server request running, so the UI
 * must preserve the same scan and idempotency key for a safe manual retry.
 */
export const CONTEXTLESS_DROPOFF_OBSERVATION_TIMEOUT_MS = 35000;

const CONTEXTLESS_DROPOFF_RECOVERY_BY_ERROR_CODE = {
  contextless_dropoff_unavailable: "retry_same_attempt",
  door_command_outcome_unknown: "manual_door_verification",
  // Older Admin versions could return this after an ambiguous door dispatch.
  door_command_unavailable: "manual_door_verification",
  forbidden: "safe_exit",
  idempotency_conflict: "safe_exit",
  idempotency_in_progress: "retry_same_attempt",
  idempotency_state_unavailable: "retry_same_attempt",
  invalid_contextless_dropoff_request: "safe_exit",
  invalid_idempotency_key: "safe_exit",
  invalid_virtual_response: "retry_same_attempt",
  not_found: "safe_exit",
  reservation_not_eligible: "retry_same_attempt",
  unauthorized: "safe_exit",
  virtual_auth_unavailable: "safe_exit",
  virtual_contextless_dropoff_disabled: "safe_exit",
  virtual_eligibility_changed: "retry_same_attempt",
  virtual_not_configured: "safe_exit",
  // Current Admin reserves this code for rejection before provider I/O.
  virtual_not_eligible: "safe_exit",
  virtual_request_rejected: "safe_exit",
  virtual_unavailable: "retry_same_attempt",
} as const satisfies Record<string, ContextlessDropoffRecovery>;

type KnownContextlessDropoffErrorCode = keyof typeof CONTEXTLESS_DROPOFF_RECOVERY_BY_ERROR_CODE;

const isKnownContextlessDropoffErrorCode = (
  value: unknown
): value is KnownContextlessDropoffErrorCode =>
  typeof value === "string" &&
  Object.prototype.hasOwnProperty.call(CONTEXTLESS_DROPOFF_RECOVERY_BY_ERROR_CODE, value);

const isContextlessDropoffResponse = (value: unknown): value is ContextlessDropoffResponse => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<ContextlessDropoffResponse>;
  const validStatus = candidate.status === "deposited" || candidate.status === "already_deposited";
  return (
    validStatus &&
    typeof candidate.reservation_id === "string" &&
    candidate.reservation_id.trim().length > 0 &&
    typeof candidate.locker_id === "string" &&
    candidate.locker_id.trim().length > 0 &&
    typeof candidate.door_position === "string" &&
    candidate.door_position === candidate.door_position.trim() &&
    /^[A-Za-z][0-9]+$/.test(candidate.door_position)
  );
};

const getErrorCode = async (
  response: Response
): Promise<KnownContextlessDropoffErrorCode | undefined> => {
  try {
    const payload = (await response.json()) as { detail?: { code?: string } };
    const code = payload?.detail?.code;
    return isKnownContextlessDropoffErrorCode(code) ? code : undefined;
  } catch {
    return undefined;
  }
};

export const createContextlessDropoffIdempotencyKey = (lockerId: string) => {
  const randomId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return `locker-contextless-${lockerId}-${randomId}`;
};

export const createContextlessDropoff = async ({
  scan,
  idempotencyKey,
  timeoutMs = CONTEXTLESS_DROPOFF_OBSERVATION_TIMEOUT_MS,
  onProgress,
  progressSignal,
}: CreateContextlessDropoffRequest): Promise<ContextlessDropoffResponse> => {
  const { apiUrl, apiKey, lockerId } = getConfig();
  const url = buildUrl(apiUrl, "/v2/locker/contextless-dropoffs/", {
    locker_group_id: lockerId,
  });
  const controller = new AbortController();
  const stopProgress = onProgress
    ? observeProgress(
        buildUrl(apiUrl, "/v2/locker/contextless-dropoffs/progress/", {
          locker_group_id: lockerId,
        }),
        {
          Accept: "application/json",
          "X-Locker-Private-Key": apiKey,
          "Idempotency-Key": idempotencyKey,
        },
        onProgress,
        progressSignal
      )
    : () => undefined;
  const timeoutId = setTimeout(() => {
    stopProgress();
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetch(url, {
      method: "POST",
      cache: "no-store",
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Locker-Private-Key": apiKey,
        "Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify({ scan, open_door: false }),
    });

    if (!response.ok) {
      const code = await getErrorCode(response);
      const recovery = code
        ? CONTEXTLESS_DROPOFF_RECOVERY_BY_ERROR_CODE[code]
        : response.status === 408 ||
            response.status === 409 ||
            response.status === 429 ||
            response.status >= 500
          ? "retry_same_attempt"
          : "safe_exit";
      throw new ContextlessDropoffError({
        kind: "http",
        status: response.status,
        code,
        recovery,
      });
    }

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new ContextlessDropoffError({
        kind: "invalid_response",
        recovery: "retry_same_attempt",
      });
    }
    if (!isContextlessDropoffResponse(payload)) {
      throw new ContextlessDropoffError({
        kind: "invalid_response",
        recovery: "retry_same_attempt",
      });
    }

    return payload;
  } catch (error) {
    if (error instanceof ContextlessDropoffError) {
      throw error;
    }

    if ((error as { name?: string } | undefined)?.name === "AbortError") {
      throw new ContextlessDropoffError({
        kind: "timeout",
        recovery: "retry_same_attempt",
      });
    }

    throw new ContextlessDropoffError({
      kind: "network",
      recovery: "retry_same_attempt",
    });
  } finally {
    stopProgress();
    clearTimeout(timeoutId);
  }
};
